<template>
  <div class="dashboard-container">
    <!-- ===== Header ===== -->
    <div class="dash-header">
      <div class="header-left">
        <el-icon class="head-icon" :size="22"><Odometer /></el-icon>
        <h2>仪表盘</h2>
        <el-tag :type="isAdmin ? 'danger' : 'primary'" size="small" effect="plain">
          {{ roleLabel }}
        </el-tag>
        <el-tag v-if="!isAdmin" type="info" size="small" effect="plain">仅我的客户</el-tag>
        <span class="welcome-text">欢迎回来，{{ summary.user?.uname || '' }}</span>
      </div>
      <div class="header-right">
        <span class="rate-badge">
          <el-icon :size="14"><Connection /></el-icon>
          USD/CNY <b>{{ summary.exchangeRate?.rate?.toFixed(2) || '-' }}</b>
        </span>
        <el-button size="small" icon="Refresh" :loading="loading" @click="loadAll">刷新</el-button>
      </div>
    </div>

    <!-- ===== KPI 行 ===== -->
    <el-row :gutter="14" class="kpi-row">
      <el-col :xs="12" :sm="12" :lg="6" v-for="card in kpiCards" :key="card.key">
        <div class="stat-card" :style="{ borderTopColor: card.color }">
          <div class="stat-main">
            <div class="stat-value">
              <span class="stat-prefix">{{ card.prefix }}</span>{{ fmtNum(card.value) }}<span class="stat-suffix">{{ card.suffix }}</span>
            </div>
            <div class="stat-label">{{ card.label }}</div>
          </div>
          <div class="stat-icon-wrap" :style="{ background: card.color + '1a', color: card.color }">
            <el-icon :size="26"><component :is="card.icon" /></el-icon>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- ===== 主区 ===== -->
    <el-row :gutter="14" class="main-row">
      <!-- 左栏 -->
      <el-col :xs="24" :lg="15" class="col-left">
        <!-- 我的待办 -->
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title">
                <el-icon :size="16" class="title-icon"><AlarmClock /></el-icon>
                <span>我的待办</span>
                <el-tag v-if="todoList.length" size="small" type="danger" effect="light">{{ todoList.length }}</el-tag>
              </div>
            </div>
          </template>
          <div class="scroll-list todo-list">
            <div v-for="t in todoList" :key="t.id" class="list-item" :class="{ 'is-overdue': t.kind === 'urgent' || t.overdue }">
              <div class="item-main">
                <div class="item-top">
                  <a class="link-name" @click="openCustomer(t.companyid)">{{ t.companyname }}</a>
                  <el-tag size="small" :type="methodType(t.method)" effect="plain">{{ methodLabel(t.method) }}</el-tag>
                  <el-tag v-if="t.kind === 'urgent' || t.overdue" size="small" type="danger" effect="dark">
                    超期 {{ t.overdueDays ?? 0 }} 天
                  </el-tag>
                </div>
                <div class="item-content">{{ truncate(t.content, 60) }}</div>
                <div class="item-bottom">
                  <span class="item-date">{{ t.nextfollowup ? fmtDate(t.nextfollowup) : '未设下次跟进' }}</span>
                </div>
              </div>
              <div class="item-actions">
                <el-tooltip content="标记为无效客户" placement="top">
                  <el-button size="small" type="warning" link @click="setTrackType(t.companyid, 1)">标无效</el-button>
                </el-tooltip>
                <el-tooltip content="转为长线跟踪" placement="top">
                  <el-button size="small" type="info" link @click="setTrackType(t.companyid, 2)">标长线</el-button>
                </el-tooltip>
                <el-button size="small" type="success" link @click="markDone(t)">完成</el-button>
              </div>
            </div>
            <el-empty v-if="!todoList.length" description="暂无待办" :image-size="70" />
          </div>
        </el-card>

        <!-- 我的商机 -->
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title">
                <el-icon :size="16" class="title-icon"><Suitcase /></el-icon>
                <span>我的商机</span>
                <el-tag v-if="sortedOpps.length" size="small" type="primary" effect="light">{{ sortedOpps.length }}</el-tag>
              </div>
              <router-link to="/opportunities" class="more-link">查看全部商机 <el-icon :size="12"><ArrowRight /></el-icon></router-link>
            </div>
          </template>
          <div class="scroll-list opp-list">
            <div v-for="o in sortedOpps" :key="o.id" class="list-item opp-item">
              <div class="item-main">
                <div class="item-top">
                  <a class="link-name opp-name" @click="openOpportunity(o)">{{ o.opname }}</a>
                  <el-tag size="small" :type="levelType(o.eff_class || o.opclass)" effect="plain">{{ effClassLabel(o) }}</el-tag>
                  <el-tag size="small" :type="o.opstatus === '0' ? 'success' : 'primary'" effect="plain">
                    {{ o.opstatus === '0' ? '已成交' : '进行中' }}
                  </el-tag>
                  <span v-if="o.overdue_level === 'overdue'" class="opp-warn text-danger">已超期</span>
                  <span v-else-if="o.overdue_level === 'warn'" class="opp-warn text-warn">待催办</span>
                </div>
                <div class="opp-customer">
                  <a class="link-name sub-link" @click="openCustomer(o.customercompanyid)">{{ o.companyname }}</a>
                  <span class="opp-amount">¥{{ fmtNum(o.opamount) }}</span>
                  <span class="opp-level">
                    <el-icon v-for="n in 5" :key="n" :size="12" :class="n <= o.oplevel ? 'star-on' : 'star-off'"><StarFilled /></el-icon>
                  </span>
                </div>
              </div>
              <div class="item-actions">
                <el-button size="small" type="primary" plain @click="openFollowDialog(o)">跟进</el-button>
                <el-dropdown trigger="click" @command="(cmd) => handleOppCommand(o, cmd)">
                  <el-button size="small" link type="info">标注<el-icon class="el-icon--right"><ArrowDown /></el-icon></el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item v-for="n in 5" :key="n" :command="'lvl-' + n" :disabled="o.oplevel === n">
                        <el-icon :size="13" :class="n <= o.oplevel ? 'star-on' : 'star-off'"><StarFilled /></el-icon>
                        {{ n }} 级优先级
                      </el-dropdown-item>
                      <el-dropdown-item divided v-if="o.opstatus !== '0'" command="win">标记成交</el-dropdown-item>
                      <el-dropdown-item v-else command="active">重新激活</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
            <el-empty v-if="!sortedOpps.length" description="暂无进行中商机" :image-size="70" />
          </div>
        </el-card>
      </el-col>

      <!-- 右栏 -->
      <el-col :xs="24" :lg="9" class="col-right">
        <!-- 最近动态 -->
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title">
                <el-icon :size="16" class="title-icon"><Clock /></el-icon>
                <span>最近动态</span>
                <el-tag size="small" type="info" effect="plain">仅我</el-tag>
              </div>
            </div>
          </template>
          <div class="scroll-list activity-list">
            <div v-for="a in summary.activities" :key="a.id" class="activity-item">
              <span class="act-time">{{ relativeTime(a.contactdate) }}</span>
              <div class="act-body">
                <a class="link-name sub-link" @click="a.companyid ? openCustomer(a.companyid) : null">
                  {{ a.companyname || '未关联客户' }}
                </a>
                <el-tag size="small" :type="methodType(a.method)" effect="plain">{{ methodLabel(a.method) }}</el-tag>
                <span class="act-content">{{ truncate(a.content, 46) }}</span>
              </div>
            </div>
            <el-empty v-if="!summary.activities?.length" description="暂无动态" :image-size="70" />
          </div>
        </el-card>

        <!-- 无效客户 -->
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title">
                <el-icon :size="16" class="title-icon"><CircleClose /></el-icon>
                <span>无效客户</span>
                <el-tag v-if="summary.invalid?.length" size="small" type="danger" effect="light">{{ summary.invalid.length }}</el-tag>
              </div>
            </div>
          </template>
          <div class="scroll-list track-list">
            <div v-for="c in summary.invalid" :key="c.companyid" class="list-item">
              <div class="item-main">
                <a class="link-name" @click="openCustomer(c.companyid)">{{ c.companyname }}</a>
                <div class="track-meta">
                  <el-tag size="small" :type="levelType(c.class)" effect="plain">{{ c.class || 'C' }}</el-tag>
                  <span class="track-country">{{ c.country }}</span>
                  <span class="item-date">最后联系 {{ fmtDate(c.lastcontactdate) }}</span>
                </div>
              </div>
              <div class="item-actions">
                <el-button size="small" type="success" link @click="setTrackType(c.companyid, 0)">恢复跟进</el-button>
              </div>
            </div>
            <el-empty v-if="!summary.invalid?.length" description="无无效客户" :image-size="70" />
          </div>
        </el-card>

        <!-- 长线跟踪 -->
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title">
                <el-icon :size="16" class="title-icon"><Timer /></el-icon>
                <span>长线跟踪</span>
                <el-tag v-if="summary.longterm?.length" size="small" type="warning" effect="light">{{ summary.longterm.length }}</el-tag>
              </div>
            </div>
          </template>
          <div class="scroll-list track-list">
            <div v-for="c in summary.longterm" :key="c.companyid" class="list-item">
              <div class="item-main">
                <a class="link-name" @click="openCustomer(c.companyid)">{{ c.companyname }}</a>
                <div class="track-meta">
                  <el-tag size="small" :type="levelType(c.class)" effect="plain">{{ c.class || 'C' }}</el-tag>
                  <span class="track-country">{{ c.country }}</span>
                  <span class="item-date">最后联系 {{ fmtDate(c.lastcontactdate) }}</span>
                </div>
              </div>
              <div class="item-actions">
                <el-button size="small" type="primary" link @click="setTrackType(c.companyid, 0)">转正常</el-button>
                <el-button size="small" type="danger" link @click="setTrackType(c.companyid, 1)">标无效</el-button>
              </div>
            </div>
            <el-empty v-if="!summary.longterm?.length" description="无长线客户" :image-size="70" />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- ===== 底栏（admin/manager）：销售漏斗 + 订单状态 + 团队业绩 ===== -->
    <el-row :gutter="14" class="bottom-row" v-if="isAdmin">
      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title"><el-icon :size="16" class="title-icon"><Histogram /></el-icon><span>销售漏斗</span></div>
            </div>
          </template>
          <div class="funnel-wrap">
            <div class="funnel-stage" v-for="(s,i) in funnelStages" :key="s.key">
              <div class="funnel-bar" :style="{ background: s.color, height: s.h + '%' }">
                <b>{{ s.value }}</b>
              </div>
              <span class="funnel-label">{{ s.label }}</span>
            </div>
          </div>
          <div class="funnel-rates">
            <span v-for="r in funnelRates" :key="r.key">{{ r.label }} <b>{{ r.pct }}%</b></span>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title"><el-icon :size="16" class="title-icon"><Box /></el-icon><span>订单状态</span></div>
            </div>
          </template>
          <div class="order-status-grid">
            <div v-for="s in orderStatusList" :key="s.key" class="order-status-item">
              <span class="os-dot" :style="{ background: s.color }"></span>
              <span class="os-label">{{ s.label }}</span>
              <b class="os-count">{{ summary.orderStatus?.[s.key] || 0 }}</b>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="section-card">
          <template #header>
            <div class="card-header">
              <div class="card-title"><el-icon :size="16" class="title-icon"><UserFilled /></el-icon><span>团队业绩</span></div>
            </div>
          </template>
          <div class="team-list">
            <div v-for="t in summary.team" :key="t.uid" class="team-row">
              <span class="team-name">{{ t.uname }}</span>
              <span class="team-stat">客户 {{ t.custCount }}</span>
              <span class="team-stat">商机 {{ t.oppCount }}</span>
              <span class="team-amount">¥{{ fmtNum(t.closedAmount) }}</span>
              <el-progress :percentage="teamPercent(t)" :stroke-width="6" :show-text="false" />
            </div>
            <el-empty v-if="!summary.team?.length" description="暂无团队数据" :image-size="60" />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- ===== 跟进 Dialog ===== -->
    <el-dialog v-model="followDialog" title="添加跟进记录" width="480px" :close-on-click-modal="false">
      <el-form label-width="84px" label-position="left">
        <el-form-item label="商机">
          <span class="dialog-opp">{{ currentOpp?.opname }}</span>
        </el-form-item>
        <el-form-item label="跟进方式">
          <el-select v-model="followForm.method" style="width: 100%">
            <el-option v-for="m in methodOptions" :key="m.value" :label="m.label" :value="m.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="跟进内容">
          <el-input v-model="followForm.content" type="textarea" :rows="3" placeholder="记录本次沟通内容" maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="跟进日期">
          <el-date-picker v-model="followForm.contactdate" type="datetime" placeholder="选择日期时间" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
        </el-form-item>
        <el-form-item label="下次跟进">
          <el-date-picker v-model="followForm.nextfollowup" type="date" placeholder="选填" value-format="YYYY-MM-DD" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="followDialog = false">取消</el-button>
        <el-button type="primary" :loading="savingFollow" @click="submitFollow">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { post } from '@/api/index'
import { ElMessage } from 'element-plus'

const router = useRouter()
const loading = ref(false)
const summary = ref({})
const myOpportunities = ref([])

const isAdmin = computed(() => ['admin', 'manager'].includes(summary.value.user?.role))
const roleLabel = computed(() => isAdmin.value ? '经理' : '业务员')

const methodOptions = [
  { value: 'phone', label: '电话' },
  { value: 'email', label: '邮件' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'wechat', label: '微信' },
  { value: 'visit', label: '拜访' },
  { value: 'meeting', label: '面谈' },
  { value: 'exhibition', label: '展会' },
  { value: 'other', label: '其他' }
]

/* ---------------- KPI ---------------- */
const kpiCards = computed(() => {
  const k = summary.value.kpi || {}
  return [
    {
      key: 'closed', label: '本月成交额', value: k.monthClosedAmount, prefix: '¥', suffix: '', color: '#409eff', icon: 'Money',
      sub: k.monthTarget ? `目标 ${Math.round((k.monthClosedAmount || 0) / k.monthTarget * 100)}%` : ''
    },
    {
      key: 'opps', label: '进行中商机', value: k.activeOpps, prefix: '', suffix: ' 个', color: '#67c23a', icon: 'Suitcase',
      sub: `本月新客户 ${k.monthNewCustomers || 0}`
    },
    {
      key: 'today', label: '今日待跟进', value: k.todayTasks, prefix: '', suffix: ' 项', color: '#e6a23c', icon: 'AlarmClock',
      sub: `本周待办 ${k.weekTasks || 0} 项`
    },
    {
      key: 'overdue', label: '超期未跟进', value: k.overdueTasks, prefix: '', suffix: ' 个', color: '#f56c6c', icon: 'Warning',
      sub: `待发货 ${k.pendingShip || 0} 单`
    }
  ]
})

/* ---------------- 待办合并 ---------------- */
const todoList = computed(() => {
  const seen = new Map()
  ;(summary.value.tasks || []).forEach(t => seen.set(t.id, { ...t, kind: 'task' }))
  ;(summary.value.urgent || []).forEach(t => seen.set(t.id, { ...t, kind: 'urgent' }))
  const list = [...seen.values()]
  list.sort((a, b) => {
    const aUrgent = a.kind === 'urgent' ? 1 : 0
    const bUrgent = b.kind === 'urgent' ? 1 : 0
    if (aUrgent !== bUrgent) return bUrgent - aUrgent
    const da = a.nextfollowup ? +new Date(a.nextfollowup) : Infinity
    const db = b.nextfollowup ? +new Date(b.nextfollowup) : Infinity
    return da - db
  })
  return list
})

/* ---------------- 商机排序 ---------------- */
const sortedOpps = computed(() => {
  const rank = { overdue: 0, warn: 1, normal: 2 }
  return [...myOpportunities.value].sort((a, b) => {
    const ra = rank[a.overdue_level] ?? 3
    const rb = rank[b.overdue_level] ?? 3
    if (ra !== rb) return ra - rb
    return (b.gap_days || 0) - (a.gap_days || 0)
  })
})

/* ---------------- 漏斗 / 订单 / 团队 ---------------- */
const funnelStages = computed(() => {
  const f = summary.value.funnel || {}
  const max = Math.max(f.customers || 1, f.opportunities || 1, f.quotations || 1, f.orders || 1)
  return [
    { key: 'customers', label: '客户', value: f.customers || 0, color: '#409eff', h: Math.max(20, Math.round((f.customers || 1) / max * 100)) },
    { key: 'opportunities', label: '商机', value: f.opportunities || 0, color: '#67c23a', h: Math.max(20, Math.round((f.opportunities || 1) / max * 100)) },
    { key: 'quotations', label: '报价', value: f.quotations || 0, color: '#e6a23c', h: Math.max(20, Math.round((f.quotations || 1) / max * 100)) },
    { key: 'orders', label: '订单', value: f.orders || 0, color: '#f56c6c', h: Math.max(20, Math.round((f.orders || 1) / max * 100)) }
  ]
})

const funnelRates = computed(() => {
  const f = summary.value.funnel || {}
  const c2o = f.customers ? Math.round(f.opportunities / f.customers * 100) : 0
  const o2q = f.opportunities ? Math.round(f.quotations / f.opportunities * 100) : 0
  const q2d = f.quotations ? Math.round(f.orders / f.quotations * 100) : 0
  return [
    { key: 'c2o', label: '客户→商机', pct: c2o },
    { key: 'o2q', label: '商机→报价', pct: o2q },
    { key: 'q2d', label: '报价→订单', pct: q2d }
  ]
})

const orderStatusList = [
  { key: 'draft', label: '草稿', color: '#909399' },
  { key: 'confirmed', label: '已确认', color: '#409eff' },
  { key: 'producing', label: '生产中', color: '#e6a23c' },
  { key: 'shipped', label: '已发货', color: '#67c23a' },
  { key: 'completed', label: '已完成', color: '#67c23a' },
  { key: 'cancelled', label: '已取消', color: '#f56c6c' }
]

/* ---------------- 数据加载 ---------------- */
async function loadAll() {
  loading.value = true
  try {
    const [rawSummary, rawOpps] = await Promise.all([
      post('dashboardapi.asp', { action: 'summary' }),
      post('opportunitiesapi.asp', { action: 'list', status: 'active', size: '10' })
    ])
    summary.value = parseRes(rawSummary)
    const oppRes = parseRes(rawOpps)
    myOpportunities.value = (oppRes && oppRes.list) || []
  } catch (e) {
    console.error('dashboard load error', e)
  } finally {
    loading.value = false
  }
}

function parseRes(raw) {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try { return JSON.parse(raw.trim()) } catch (e) { return {} }
  }
  return raw
}

/* ---------------- 导航 ---------------- */
function openCustomer(companyid) {
  if (!companyid) return
  router.push({ path: '/customers', query: { companyid } })
}

function openOpportunity(opp) {
  if (!opp || !opp.id) return
  router.push({ path: '/opportunities', query: { opportunityid: opp.id } })
}

/* ---------------- 商机快捷操作 ---------------- */
async function setOppLevel(opp, level) {
  try {
    const res = parseRes(await post('opportunitiesapi.asp', { action: 'updatelevel', id: opp.id, oplevel: level }))
    if (res && res.result === 'done') {
      ElMessage.success(`已将「${opp.opname}」设为 ${level} 级`)
      await loadAll()
    } else {
      ElMessage.error(res?.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error('操作失败')
  }
}

async function setOppStatus(opp, opstatus) {
  const label = opstatus === 0 ? '成交' : '激活'
  try {
    const res = parseRes(await post('opportunitiesapi.asp', { action: 'save', id: opp.id, opstatus }))
    if (res && res.result === 'done') {
      ElMessage.success(`已标记「${opp.opname}」为${label}`)
      await loadAll()
    } else {
      ElMessage.error(res?.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error('操作失败')
  }
}

function handleOppCommand(opp, cmd) {
  if (cmd.startsWith('lvl-')) {
    setOppLevel(opp, parseInt(cmd.slice(4), 10))
  } else if (cmd === 'win') {
    setOppStatus(opp, 0)
  } else if (cmd === 'active') {
    setOppStatus(opp, 1)
  }
}

/* ---------------- 跟进 Dialog ---------------- */
const followDialog = ref(false)
const savingFollow = ref(false)
const currentOpp = ref(null)
const followForm = ref({})

function nowStr() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

function openFollowDialog(opp) {
  currentOpp.value = opp
  followForm.value = { method: 'email', content: '', contactdate: nowStr(), nextfollowup: '' }
  followDialog.value = true
}

async function submitFollow() {
  if (!followForm.value.content.trim()) {
    ElMessage.warning('请填写跟进内容')
    return
  }
  savingFollow.value = true
  try {
    const res = parseRes(await post('opportunitiesapi.asp', {
      action: 'addfollow',
      opportunityid: currentOpp.value.id,
      method: followForm.value.method,
      content: followForm.value.content.trim(),
      contactdate: followForm.value.contactdate,
      nextfollowup: followForm.value.nextfollowup || ''
    }))
    if (res && res.result === 'done') {
      ElMessage.success('跟进已记录')
      followDialog.value = false
      await loadAll()
    } else {
      ElMessage.error(res?.message || '跟进失败')
    }
  } catch (e) {
    ElMessage.error('跟进失败')
  } finally {
    savingFollow.value = false
  }
}

/* ---------------- 客户跟踪性质 ---------------- */
async function setTrackType(companyid, tracktype) {
  const label = { 0: '正常跟进', 1: '无效', 2: '长线' }[tracktype] || tracktype
  try {
    const res = parseRes(await post('customersapi.asp', { action: 'set_tracktype', companyid, tracktype }))
    if (res && res.result === 'done') {
      ElMessage.success(`已标记为${label}`)
      await loadAll()
    } else {
      ElMessage.error(res?.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error('操作失败')
  }
}

/* ---------------- 完成（本地） ---------------- */
function markDone(task) {
  ElMessage.success(`已完成: ${task.companyname}`)
  if (summary.value.tasks) {
    summary.value.tasks = summary.value.tasks.filter(t => t.id !== task.id)
  }
  if (summary.value.urgent) {
    summary.value.urgent = summary.value.urgent.filter(t => t.id !== task.id)
  }
  if (summary.value.kpi) {
    summary.value.kpi.todayTasks = Math.max(0, (summary.value.kpi.todayTasks || 0) - 1)
  }
}

/* ---------------- 团队进度 ---------------- */
function teamPercent(t) {
  const max = Math.max(...(summary.value.team || []).map(x => x.closedAmount || 0), 1)
  return Math.round((t.closedAmount || 0) / max * 100)
}

/* ---------------- 格式化 ---------------- */
function fmtNum(v) {
  if (v == null) return '0'
  return Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })
}
function fmtDate(d) {
  if (!d) return '-'
  return String(d).slice(0, 10)
}
function truncate(s, n) {
  if (!s) return ''
  return s.length > n ? s.slice(0, n) + '...' : s
}
function relativeTime(d) {
  if (!d) return ''
  const diff = (Date.now() - new Date(d).getTime()) / 1000
  if (diff < 60) return '刚刚'
  if (diff < 3600) return Math.floor(diff / 60) + '分钟前'
  if (diff < 86400) return Math.floor(diff / 3600) + '小时前'
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + '天前'
  return fmtDate(d)
}
function methodType(m) {
  const map = { phone: '', email: 'primary', whatsapp: 'success', wechat: 'success', meeting: 'warning', visit: 'warning', exhibition: 'danger' }
  return map[m] || 'info'
}
function methodLabel(m) {
  const map = { phone: '电话', email: '邮件', whatsapp: 'WhatsApp', wechat: '微信', meeting: '面谈', visit: '拜访', exhibition: '展会' }
  return map[m] || m
}
function levelType(l) {
  return l === 'A' ? 'danger' : l === 'B' ? 'warning' : 'info'
}
function effClassLabel(o) {
  const l = o.eff_class || o.opclass
  const lvMap = { A: 'A 高', B: 'B 中', C: 'C 常规', 1: 'A 高', 2: 'B 中', 3: 'C 常规' }
  return lvMap[l] || l || 'C'
}

onMounted(loadAll)
</script>

<style scoped>
.dashboard-container { padding: 16px 20px; background: #f5f7fa; min-height: calc(100vh - 60px); }

/* ---- Header ---- */
.dash-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.dash-header h2 { margin: 0 4px 0 8px; font-size: 20px; }
.header-left, .header-right { display: flex; align-items: center; gap: 8px; }
.head-icon { color: #409eff; }
.welcome-text { font-size: 13px; color: #909399; margin-left: 4px; }
.rate-badge { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; padding: 4px 12px; border-radius: 20px; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.rate-badge b { font-size: 15px; }

/* ---- KPI ---- */
.kpi-row { margin-bottom: 14px; }
.stat-card { background: #fff; border-radius: 10px; padding: 16px 18px; border-top: 3px solid #409eff; box-shadow: 0 1px 4px rgba(0,0,0,.05); margin-bottom: 10px; display: flex; justify-content: space-between; align-items: center; }
.stat-value { font-size: 24px; font-weight: 700; color: #303133; line-height: 1.2; }
.stat-prefix { font-size: 15px; font-weight: 600; margin-right: 2px; }
.stat-suffix { font-size: 14px; font-weight: 500; color: #909399; margin-left: 2px; }
.stat-label { font-size: 13px; color: #909399; margin-top: 4px; }
.stat-icon-wrap { width: 46px; height: 46px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* ---- 卡片 ---- */
.main-row { margin-bottom: 14px; }
.section-card { margin-bottom: 14px; border-radius: 10px; }
.section-card :deep(.el-card__header) { padding: 12px 16px; border-bottom: 1px solid #f0f2f5; }
.section-card :deep(.el-card__body) { padding: 8px 16px 12px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.card-title { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 14px; }
.title-icon { color: #409eff; }
.more-link { font-size: 12px; color: #409eff; display: inline-flex; align-items: center; gap: 2px; text-decoration: none; }

/* ---- 滚动列表 ---- */
.scroll-list { max-height: 380px; overflow-y: auto; }
.list-item { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; padding: 10px 2px; border-bottom: 1px solid #f5f5f5; }
.list-item:last-child { border-bottom: none; }
.list-item.is-overdue { border-left: 3px solid #f56c6c; padding-left: 10px; }
.item-main { flex: 1; min-width: 0; }
.item-top { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.item-content { font-size: 12px; color: #909399; margin: 4px 0; line-height: 1.5; }
.item-bottom { display: flex; align-items: center; gap: 8px; }
.item-date { font-size: 11px; color: #c0c4cc; }
.item-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; }

.link-name { color: #303133; font-weight: 600; font-size: 14px; cursor: pointer; }
.link-name:hover { color: #409eff; }
.sub-link { font-weight: 500; font-size: 13px; }
.text-danger { color: #f56c6c; }
.text-warn { color: #e6a23c; }

/* ---- 商机 ---- */
.opp-item { align-items: center; }
.opp-name { font-size: 14px; }
.opp-customer { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.opp-amount { font-size: 13px; font-weight: 600; color: #e6a23c; }
.opp-level { display: inline-flex; gap: 1px; }
.opp-warn { font-size: 12px; margin-left: 2px; }
.star-on { color: #e6a23c; }
.star-off { color: #dcdfe6; }

/* ---- 动态 ---- */
.activity-list { max-height: 300px; }
.activity-item { display: flex; gap: 10px; padding: 9px 2px; border-bottom: 1px solid #fafafa; align-items: flex-start; }
.activity-item:last-child { border-bottom: none; }
.act-time { color: #c0c4cc; font-size: 11px; flex-shrink: 0; min-width: 52px; padding-top: 2px; }
.act-body { flex: 1; min-width: 0; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.act-content { color: #909399; font-size: 12px; }

/* ---- 无效/长线 ---- */
.track-list { max-height: 260px; }
.track-meta { display: flex; align-items: center; gap: 6px; margin-top: 4px; flex-wrap: wrap; }
.track-country { font-size: 12px; color: #909399; }

/* ---- 底部三卡 ---- */
.bottom-row { margin-bottom: 14px; }
.funnel-wrap { display: flex; align-items: flex-end; justify-content: space-around; gap: 16px; height: 150px; padding: 0 12px; }
.funnel-stage { text-align: center; min-width: 50px; flex: 1; }
.funnel-bar { border-radius: 6px 6px 0 0; color: #fff; font-size: 16px; display: flex; align-items: flex-start; justify-content: center; padding-top: 6px; transition: height .3s; }
.funnel-label { display: block; margin-top: 6px; font-size: 12px; color: #606266; }
.funnel-rates { display: flex; justify-content: space-around; padding: 10px 8px 0; font-size: 12px; color: #909399; }
.funnel-rates b { color: #303133; }

.order-status-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px 16px; padding: 4px 0; }
.order-status-item { display: flex; align-items: center; gap: 6px; }
.os-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.os-label { font-size: 13px; color: #606266; flex: 1; }
.os-count { font-size: 15px; color: #303133; }

.team-list { display: flex; flex-direction: column; gap: 12px; padding: 4px 0; }
.team-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.team-name { font-weight: 600; min-width: 56px; }
.team-stat { font-size: 12px; color: #909399; }
.team-amount { font-weight: 600; color: #409eff; margin-left: auto; }
.team-row .el-progress { width: 100%; }

/* ---- Dialog ---- */
.dialog-opp { font-weight: 600; }

@media (max-width: 767px) {
  .dashboard-container { padding: 10px; }
  .stat-value { font-size: 20px; }
  .dash-header { flex-direction: column; align-items: flex-start; gap: 8px; }
  .welcome-text { display: none; }
  .funnel-wrap { height: 100px; gap: 10px; padding: 0 6px; }
  .funnel-bar { font-size: 13px; }
  .order-status-grid { grid-template-columns: repeat(3, 1fr); }
}
</style>
